import { Attribute, Component, OnDestroy, OnInit } from '@angular/core';
import { ColDef, GridOptions, RowClickedEvent } from 'ag-grid';
import { Subscription } from 'rxjs/Subscription';
import {
  IComplaintsDashboardView,
  IDashboardRankRow,
} from '../../../../../core/interfaces/complaints-dashboard.interface';
import { ComplaintsDashboardStore } from '../../complaints-dashboard.store';

export type RankTableDimension = 'provider' | 'area';

interface IRankTableConfig {
  heading: string;
  nameHeader: string;
  rows: (view: IComplaintsDashboardView) => IDashboardRankRow[];
}

interface IRankRowParams {
  data: IDashboardRankRow;
}

const RANK_TABLES: { [dimension: string]: IRankTableConfig } = {
  provider: {
    heading: 'Transportation Providers',
    nameHeader: 'Provider',
    rows: (view) => view.provRows,
  },
  area: {
    heading: 'Service Areas',
    nameHeader: 'Service Area',
    rows: (view) => view.areaRows,
  },
};

const RIGHT_ALIGN_CLASS = 'text-a-right';
const NUMERIC_COLUMN: ColDef = {
  width: 78,
  suppressSizeToFit: true,
  cellClass: [RIGHT_ALIGN_CLASS],
};
const NAME_COLUMN_WIDTH = 150;
const TONE_CLASS_PREFIX = 'cd-tone--';
const SELECTED_ROW_CLASS = 'cd-selected';
const CLICKABLE_ROW_CLASS = 'cd-clickable';
const NO_ROWS_TEXT = 'No data in this period.';

@Component({
  selector: 'app-dashboard-rank-table',
  templateUrl: './dashboard-rank-table.component.html',
  styleUrls: ['./dashboard-rank-table.component.scss'],
})
export class DashboardRankTableComponent implements OnInit, OnDestroy {
  grid: GridOptions;
  private dimension: RankTableDimension;
  private config: IRankTableConfig;
  private subscriptions: Subscription[] = [];

  constructor(
    private store: ComplaintsDashboardStore,
    // Static attribute read once; a value bound from a variable needs @Input instead.
    @Attribute('dimension') dimension: string,
  ) {
    if (!RANK_TABLES.hasOwnProperty(dimension)) {
      throw new Error(
        'Invalid rank table dimension "' +
          dimension +
          '". Expected one of: ' +
          Object.keys(RANK_TABLES).join(', ') +
          '.',
      );
    }
    this.dimension = dimension as RankTableDimension;
    this.config = RANK_TABLES[dimension];
    this.initGrid();
  }

  ngOnInit() {
    this.watchView();
  }

  select(row: IDashboardRankRow) {
    if (row.id) {
      this.store.toggle(this.dimension, row.id);
    }
  }

  private watchView() {
    this.subscriptions.push(
      this.store.view$.subscribe({
        next: (view: IComplaintsDashboardView) =>
          this.setRows(view ? this.config.rows(view) : []),
      }),
    );
  }

  private setRows(rows: IDashboardRankRow[]) {
    this.grid.rowData = rows;

    if (this.grid.api) {
      this.grid.api.setRowData(rows);
    }
  }

  private numericColumn(headerName: string, field: string): ColDef {
    return Object.assign({}, NUMERIC_COLUMN, { headerName, field });
  }

  private initGrid() {
    this.grid = <GridOptions>{
      enableColResize: true,
      enableSorting: false,
      enableFilter: false,
      suppressCellSelection: true,
      defaultColDef: {
        suppressMenu: true,
        suppressSorting: true,
      },
      columnDefs: [
        {
          headerName: this.config.nameHeader,
          field: 'label',
          width: NAME_COLUMN_WIDTH,
          tooltipField: 'label',
        },
        this.numericColumn('Trips', 'trips'),
        this.numericColumn('Total', 'total'),
        this.numericColumn('Rate', 'rate'),
        this.numericColumn('Pending', 'pending'),
        this.numericColumn('Valid', 'valid'),
        Object.assign(this.numericColumn('vs Prior', 'delta'), {
          cellClass: (params: IRankRowParams) => [
            RIGHT_ALIGN_CLASS,
            TONE_CLASS_PREFIX + params.data.dTone,
          ],
        }),
      ],
      rowClassRules: {
        [SELECTED_ROW_CLASS]: (params: IRankRowParams) => params.data.selected,
        [CLICKABLE_ROW_CLASS]: (params: IRankRowParams) => !!params.data.id,
      },
      rowData: [],
      overlayNoRowsTemplate: NO_ROWS_TEXT,
      onRowClicked: (event: RowClickedEvent) => this.select(event.data),
      onGridReady: (params) => {
        params.api.setRowData(this.grid.rowData || []);
        params.api.sizeColumnsToFit();
      },
      onModelUpdated: (params) => {
        params.api.sizeColumnsToFit();
      },
      onGridSizeChanged: (params) => {
        params.api.sizeColumnsToFit();
      },
    };
  }

  get heading(): string {
    return this.config.heading;
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/complaints/complaints-dashboard/components/dashboard-rank-table/dashboard-rank-table.component.ts